import {
  Component,
  Inject,
  OnInit,
  Renderer2,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { ModalController, NavController, Platform, AlertController } from '@ionic/angular/lazy';
import { MessagingService } from 'src/providers/services/push.messaging.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import { jwtDecode } from 'jwt-decode';
import { Router, NavigationStart } from '@angular/router';
import AOS from 'aos';
import { delay } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { NotificationService } from './shared/services/notification/notification.service';
import { Browser } from '@capacitor/browser';
import { environment } from 'src/environments/environment';
import { AuthGuard } from './core/services/auth-guard.service';
import { DOCUMENT } from '@angular/common';
import { DashboardService } from './dashboard/services/dashboard.service';
import { CurrentUrlService } from './user/services/current-url.service';
import moment from 'moment';
import { App } from '@capacitor/app';
import { AiClientService } from './user/services/ai-client.service';
import { Preferences } from '@capacitor/preferences';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { TextZoom } from '@capacitor/text-zoom';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  onLogin: boolean = false;
  isSafe: boolean = false;
  isDragging = false;
  initialX: number = 0;
  initialY: number = 0;
  currentX: number = 0;
  currentY: number = 0;
  isAndroid: boolean = false;
  bookmarks: any[] = [];
  currentURL: string = '';
  tokenByPassUrl = [
    '/login',
    '/login/mobile-login',
    '/signup',
    '/auth/signup',
    '/auth/forgotPasswordComplete',
    '/auth/activate',
    '/forgot-password',
    '/tabs/contents/niwi-faqs',
    '/tabs/contents/contact-us',
    '/tabs/diet/diet-programmes',
    '/tabs/shop/package',
    '/tabs/shop/special-packages',
    '/tabs/shop/premium-packages',
    '/tabs/shop/my-cart',
    '/tabs/shop/my-checkout',
    '/tabs/shop/discount-coupon',
    '/forms',
    '/guideline',
  ];

  constructor(
    public platform: Platform,
    private messagingService: MessagingService,
    private jwtService: JwtService,
    private router: Router,
    private notificationService: NotificationService,
    private navCtrl: NavController,
    private modalController: ModalController,
    private alertController: AlertController,
    private renderer: Renderer2,
    private authGuardService: AuthGuard,
    private dashboardService: DashboardService,
    private currentUrlService: CurrentUrlService,
    private cdr: ChangeDetectorRef,
    private aiclientService: AiClientService,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: any,
  ) {
    this.currentUrlService.currentUrl.subscribe((url) => {
      setTimeout(() => {
        this.currentURL = url;
        this.cdr.detectChanges();
      }, 0);
    });

    const url = window.location.href;
    const path = url.split(environment.app.app_url)[1];
    if (path) {
      this.authGuardService.changeUrl(path);
    }

    this.isSafe = this.platform.is('ios') || this.platform.is('iphone');

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationStart) {
        this.checkForValidToken(val.url);
      }
    });

    this.jwtService.onToken.pipe(delay(1000)).subscribe((res) => {
      setTimeout(() => {
        if (res === 'tokenSet') {
          this.onLogin = true;
        } else if (res === 'tokenDeleted') {
          this.onLogin = false;
        }
        this.cdr.detectChanges();
      }, 0);
    });

    this.jwtService.getToken().then((res) => {
      setTimeout(() => {
        if (res) {
          const decoded: any = jwtDecode(res);
          if (decoded && decoded.iat) {
            const timeAfter6Hours = moment.unix(decoded.iat).add(6, 'hours');

            if (moment().isAfter(timeAfter6Hours)) {
              this.checkIfUserRated();
            }
          }
        }

        this.onLogin = !!res;
        this.cdr.detectChanges();
      }, 0);
    });
    AOS.init();
  }

  ngOnInit() {
    App.addListener('appUrlOpen', (event) => {
      this.ngZone.run(() => {
        const url = new URL(event.url);

        this.platform.ready().then(() => {
          if (url.pathname === '/login') {
            this.router.navigate(['/login']);
          } else if (url.pathname === '/diet') {
            this.router.navigate(['/tabs/diet']);
          } else if (url.pathname.startsWith('/diet/')) {
            const dietId = url.pathname.split('/')[2];
            this.router
              .navigate(['/tabs/diet'], {
                queryParams: {
                  dietId: dietId,
                },
              })
              .then((success) => {
                console.log('Navigation Success?', success);
              })
              .catch((err) => {
                console.error('Navigation Error:', err);
              });
          } else {
            console.log('Default navigation to:', url.pathname);
            this.router.navigateByUrl(url.pathname);
          }
        });
      });
    });

    if (Capacitor.getPlatform() === 'android') {
      this.isAndroid = true;
    }

    if (Capacitor.isNativePlatform()) {
      this.lockScreenOrientation();
    }

    this.platform.ready().then(() => {
      this.applyFixedTextZoom();
      this.checkAppVersion();

      if (this.platform.is('android')) {
        this.registerPushNotification();
      }
    });

    const baseUrl = window.location.origin;
    let backgroundImage = 'assets/prim-green-wall.png';
    if (
      baseUrl.includes('dev.diet.niwi.ai') ||
      baseUrl.includes('diet.niwi.ai')
    ) {
      backgroundImage = 'assets/background.png';
    }

    this.renderer.setStyle(
      this.document.body,
      'background-image',
      `url(${backgroundImage})`,
    );
    this.renderer.setStyle(this.document.body, 'background-size', 'cover');
  }

  isAIOrg: boolean = false;

  async checkForValidToken(url: string) {
    const token = await this.jwtService.getToken();
    if (token) {
      const decoded: any = jwtDecode(token);
      decoded?.is_ai_client == '1'
        ? this.aiclientService.setAIClientStatus(true)
        : this.aiclientService.setAIClientStatus(false);
      decoded?.is_ai_organization == '1'
        ? this.aiclientService.setAIOrgStatus(true)
        : this.aiclientService.setAIOrgStatus(false);
      this.isAIOrg = this.aiclientService.isAIOrg;
      this.document.body.classList.remove('default-green');
      this.document.body.classList.add(decoded?.org_theme_color || 'default-green');
      if (moment.unix(decoded.exp).isBefore(moment())) {
        this.jwtService.destroyToken().then(() => {
          this.router.navigate(['/login']);
        });
      }
    } else {
      if (!this.tokenByPassUrl.includes(url) && !this.matchUrl(url)) {
        this.router.navigate(['/login']);
      }
    }
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  matchUrl(url: string): boolean {
    return this.tokenByPassUrl.some((byPassUrl) => url.indexOf(byPassUrl) > -1);
  }

  registerPushNotification() {
    // Native push registration logic
  }

  async lockScreenOrientation() {
    try {
      await ScreenOrientation.lock({ orientation: 'portrait' });
    } catch (error) {
      console.error('Error locking screen orientation:', error);
    }
  }

  onDragStart(event: MouseEvent | TouchEvent) {
    this.isDragging = true;
    if (event instanceof MouseEvent) {
      this.initialX = event.clientX - this.currentX;
      this.initialY = event.clientY - this.currentY;
    } else {
      this.initialX = event.touches[0].clientX - this.currentX;
      this.initialY = event.touches[0].clientY - this.currentY;
    }
  }

  onDrag(event: MouseEvent | TouchEvent) {
    if (this.isDragging) {
      event.preventDefault();
      if (event instanceof MouseEvent) {
        this.currentX = event.clientX - this.initialX;
        this.currentY = event.clientY - this.initialY;
      } else {
        this.currentX = event.touches[0].clientX - this.initialX;
        this.currentY = event.touches[0].clientY - this.initialY;
      }
      this.setTranslate(this.currentX, this.currentY);
    }
  }

  onDragEnd(event: MouseEvent | TouchEvent) {
    this.isDragging = false;
  }

  setTranslate(xPos: number, yPos: number) {
    const fabElement = this.document.querySelector(
      '.chat_floating_button',
    ) as HTMLElement;
    if (fabElement) {
      this.renderer.setStyle(
        fabElement,
        'transform',
        `translate3d(${xPos}px, ${yPos}px, 0)`,
      );
    }
  }

  openChatModal() {
    // Navigate to chat route instead of opening a modal
    this.router.navigate(['/chat']);
  }

  openLink(url: string) {
    const linkUrl = url.replace(/^http:/, 'https:');
    if (linkUrl) {
      Browser.open({ url: linkUrl });
    } else {
      this.notificationService.presentToast('No Url Found', '', 'danger');
    }
  }

  getBookmarks(): void {
    if (this.isAIOrg) return;
    this.dashboardService.getBookmarks().subscribe((response) => {
      setTimeout(() => {
        if (response?.data) {
          this.bookmarks = JSON.parse(response?.data);
        } else {
          this.bookmarks = [];
        }
        this.cdr.detectChanges();
      }, 0);
    });
  }

  async checkIfUserRated() {
    const { value } = await Preferences.get({ key: 'has_rated_app' });
    if (value === 'true') {
      this.cdr.detectChanges();
    }
    else{
      this.askForRating();
    }
  }

  async askForRating() {
    const alert = await this.alertController.create({
      header: 'Enjoying the app?',
      message: 'Would you mind taking a moment to rate it? It won\'t take more than a minute. Thanks for your support!',
      buttons: [
        {
          text: 'Later',
          role: 'cancel'
        },
        {
          text: 'Rate Now',
          handler: async () => {
            await Preferences.set({ key: 'has_rated_app', value: 'true' });
            this.cdr.detectChanges();
            this.openStoreForRating();
          }
        }
      ]
    });

    await alert.present();
  }

  async openStoreForRating() {
    try {
      const info = await App.getInfo();
      const appId = info.id;
      let storeUrl = '';

      if (this.platform.is('android')) {
        storeUrl = `https://play.google.com/store/apps/details?id=${appId}`;
      } else if (this.platform.is('ios') || this.platform.is('iphone') || this.platform.is('ipad')) {
        storeUrl = `https://apps.apple.com/app/6496686173?action=write-review`;
      }

      if (storeUrl) {
        await Browser.open({ url: storeUrl });
      } else {
        this.notificationService.presentToast('Not running on a supported mobile platform', '', 'warning');
      }
    } catch (error) {
      console.error('Failed to open store URL:', error);
      this.notificationService.presentToast('Unable to open store link', '', 'danger');
    }
  }

  async checkAppVersion() {
    console.log('check');

    if (!Capacitor.isNativePlatform()) return;

    try {
      const info = await AppUpdate.getAppUpdateInfo();

      if (info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        if (this.platform.is('android')) {
          await AppUpdate.performImmediateUpdate();
        } else {
          this.showForceUpdateAlert();
        }
      }
    } catch (error) {
      console.error('Error checking store for updates:', error);
    }
  }

  async showForceUpdateAlert() {
    const alert = await this.alertController.create({
      header: 'Update Required',
      message: 'A new version of the app is available. Please update to continue.',
      backdropDismiss: false,
      keyboardClose: false,
      buttons: [
        {
          text: 'UPDATE NOW',
          handler: () => {
            this.redirectToStoreForUpdate();
            return false;
          }
        }
      ]
    });

    await alert.present();
  }

  async redirectToStoreForUpdate() {
    try {
      // The plugin automatically handles opening the correct store for iOS and Android
      await AppUpdate.openAppStore();
    } catch (error) {
      console.error('Failed to open store:', error);
    }
  }

  private async applyFixedTextZoom(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Reset to 100% regardless of the device's system font setting
    await TextZoom.set({ value: 1.0 });
    console.log('[TextZoom] WebView text zoom locked to 100%');
  } catch (error) {
    console.error('[TextZoom] Failed to set text zoom:', error);
  }
}
}
